<template>
    <div>
        <div class="flex justify-between">
            <p v-if="label != null" :class="[disabled ? 'label-disabled' : '']" class="text-14 text-grey-900 pb-1 transition-all duration-300 ease-in-out">
                {{ $help.capitalizeFirstLetter(label) }}
            </p>

            <slot name="label-right" />
        </div>

        <input :name="name" :value="value" type="hidden" />

        <DropdownV2
            ref="menu"
            :buttonCSS="[
                'select-button',
                boxClass,
                dark ? 'bg-black border-grey-900/50 text-white' : '',
                disabled ? 'disabled' : '',
                error != false ? '!border-error' : '',
            ]"
            :buttonsRightClass="[
                'transition-all duration-300 ease-in-out opacity-0 delay-500 pl-2',
                [value != null && canBeCleared && !disabled ? 'group-hover:mr-0 group-hover:opacity-100' : ''],
            ]"
            :class="{
                group: value != null,
            }"
            :disabled="disabled"
            :isSelectMenu="false"
            :menuClass="['w-full']"
            :menuPush="menuPush"
            :menuSpace="0"
            :forceDirection="forceDirection"
            class="w-full relative"
            @dropdown="menuOpened"
            :hasShadow='hasShadow'
        >
            <template #button="{ open }">
                <div class="flex items-center justify-between w-full">
                    <div
                        v-if="selected != undefined && hasScopedOption == false"
                        v-html="selected.html != null ? selected.html : selected.value"
                    />

                    <template v-if="!open || !searchEnabled">
                        <slot v-if="selected != undefined" :option="selected" :selected="true" name="option" />
                        <p v-else class="text-15" :class="[disabled ? 'text-grey-600' : 'text-grey-900']">
                            {{ placeholder }}
                        </p>
                    </template>

                    <input
                        v-if="open && searchEnabled"
                        ref="searchInput"
                        :placeholder="searchPlaceholder"
                        class="w-full h-10 text-15 bg-transparent placeholder-grey-900 autofill:placeholder-grey-900"
                        type="text"
                        @keyup="searchKeyup"
                    />

                    <div class="flex">
                        <slot name="tooltip" />

                        <tippy :offset="[2, 4]" maxWidth="100%" placement="bottom-end" v-if="ai[aiKey] != null">
                            <button
                                class="w-12 h-12 flex items-center justify-center"
                                type="button"
                                @click.stop="setAIValue(ai[aiKey])"
                            >
                                <IconZap class="stroke-grey-800" />
                            </button>

                            <template #content>
                                <p>Generated by Call Assistant</p>
                            </template>
                        </tippy>

                        <button
                            class="icon-hover shrink-0 w-fit h-12 flex items-center justify-center"
                            type="button"
                            @click.stop="
                                () => {
                                    if (disabled) return

                                    $refs.menu.toggle()
                                }
                            "
                        >
                            <component
                                :is="dropdownIcon"
                                :class="[
                                    {
                                        'rotate-180': open && dropdownIcon == 'IconArrowDown',
                                    },
                                    disabled ? '!stroke-grey-900' : '',
                                ]"
                                class="transition-transform w-4.5 h-4.5"
                            />
                        </button>

                        <slot name="right" />
                    </div>
                </div>
            </template>

            <!-- <template #buttons-right>
                <div class="flex items-center">
                    <button
                        v-if="canBeCleared"
                        :disabled="value == null"
                        class="icon-hover"
                        type="button"
                        @click.stop="clear"
                    >
                        <IconClose class="w-4.5 h-4.5" />
                    </button>

                    <slot name="right" />
                </div>
            </template> -->

            <template #menu="{ open, activeDirection }">
                <div
                    :class="[menuClass, dark ? 'bg-black border-grey-900/50 text-white' : '', activeDirection]"
                    :style="{
                        'max-height': menuMaxHeight,
                    }"
                    class="select-menu"
                >
                    <button
                        v-if="hasNone"
                        class="px-3 w-full h-10 text-left text-14 outline-0 transition-colors duration-300 disabled:text-grey-900 disabled:hover:bg-transparent focus:bg-grey-500 hover:bg-grey-400"
                        type="button"
                        @click="clicked(null)"
                    >
                        {{ placeholder }}
                    </button>

                    <template v-for="(option, oi) in options">
                        <button
                            :class="[
                                buttonCSS,
                                value == option.value ? selectedCSS : '',
                                {
                                    'focus:bg-grey-500 hover:bg-grey-400': !dark,
                                    'hover:bg-white/10 focus:bg-white/10': dark,
                                },
                            ]"
                            :disabled="disabledOptionsEnabled 
                                ? option.disabled != undefined && (option.disabled == true || (typeof option == 'function' && option.disabled() == true))
                                : false"
                            class="px-3 w-full min-h-10 text-left text-14 outline-0 transition-colors duration-300 disabled:text-grey-900 disabled:hover:bg-transparent block"
                            data-close
                            type="button"
                            @click="clicked(option)"
                            v-if='option.hidden != undefined ? !option.hidden : true'
                        >
                            <!-- @blur='(oi == (options.length - 1)) ? blur() : null' -->
                            <slot :option="option" :selected="false" name="option" />

                            <div v-if="hasScopedOption == false" v-html="option.html != null ? option.html : option.value" />
                        </button>
                    </template>
                </div>
            </template>
        </DropdownV2>

        <div
            v-show="error != false"
            :class="[error == false ? 'opacity-0' : '']"
            class="pt-2 flex items-start error transition-all duration-300"
        >
            <IconFalse class="w-5 h-5 stroke-red-700 bg-red-700 rounded-full bg-opacity-10 mr-2 shrink-0" />

            <p class="error text-error text-14">{{ error }}</p>
        </div>
    </div>
</template>

<script>
import Input from '~/mixins/input.js'

export default {
    mixins: [Input],
    emits: ['update:value', 'closed'],
    props: {
        options: {
            type: Array,
            default: function () {
                return []
            },
        },
        dark: { type: Boolean, deafult: false },
        valueKey: { type: String, default: 'value' },
        searchEnabled: { type: Boolean, default: false },
        searchPlaceholder: { type: String, default: 'Search' },
        canBeCleared: { type: Boolean, deafult: false },
        selectedCSS: { type: String, default: 'font-semibold' },
        menuMaxHeight: { type: String, default: '240px' },
        menuClass: { type: String, default: '' },
        menuPush: { type: Boolean, default: false },
        dropdownIcon: { type: String, default: 'IconArrowDown' },
        hasNone: { type: Boolean, default: false },
        buttonCSS: { type: String, default: '' },
        forceDirection: { type: String, default: false },
        onSearchTriggerGetWithNull: { type: Boolean, default: false },
        disabledOptionsEnabled: { type: Boolean, default: true },
        hasShadow: { type: Boolean, default: true },
    },

    methods: {
        clicked(option) {
            this.$emit('update:value', option == null ? null : option[this.valueKey])
            this.$refs.menu.toggle()
        },

        menuOpened(open) {
            if (open) {
                setTimeout(() => {
                    this.$emit('menuOpened')

                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus()

                        if (this.onSearchTriggerGetWithNull) {
                            this.$emit('searchKeyup', '')
                        }
                    }
                })
            } else {
                this.$emit('closed')
            }
        },

        searchKeyup(event) {
            event.preventDefault()

            this.$help.delay(() => {
                this.$emit('searchKeyup', event.target.value)
            }, 150)
        },
    },

    computed: {
        selected() {
            return this.options.find((o) => o[this.valueKey] == this.value)
        },

        hasScopedOption() {
            return this.$slots.option != undefined
        },
    },
}
</script>
